import { Send } from 'lucide-react';
import { Label } from '@/components/atoms/label';
import { RichTextEditor } from '@/components/molecules/rich-text-editor';
import { FormAccordionCard, FormSectionHeader } from '@/components/molecules';
import { EstimateAttachments } from '@/components/organisms/estimates/EstimateAttachments';
import { ScopeOfWorkTemplatePicker } from '@/components/organisms/ScopeOfWorkTemplatePicker';
import type { Attachment } from '@/types/attachment';

interface EstimateCustomerContentSectionProps {
  open: boolean;
  onToggle: () => void;
  bare?: boolean;
  scopeOfWork?: string;
  onScopeOfWorkChange?: (value: string) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  isEditMode: boolean;
  entityType?: 'estimate' | 'proposal';
  editId?: string;
  pendingAttachmentId: string;
  attachments: Attachment[];
  onAttachmentsChange: (attachments: Attachment[]) => void;
}

export function EstimateCustomerContentSection({
  open,
  onToggle,
  bare = false,
  scopeOfWork,
  onScopeOfWorkChange,
  notes,
  onNotesChange,
  isEditMode,
  entityType = 'estimate',
  editId,
  pendingAttachmentId,
  attachments,
  onAttachmentsChange,
}: EstimateCustomerContentSectionProps) {
  const attachmentField =
    isEditMode && editId ? (
      entityType === 'proposal' ? (
        <EstimateAttachments
          proposalId={editId}
          attachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
        />
      ) : (
        <EstimateAttachments
          estimateId={editId}
          attachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
        />
      )
    ) : entityType === 'proposal' ? (
      <EstimateAttachments
        proposalPendingId={pendingAttachmentId}
        attachments={attachments}
        onAttachmentsChange={onAttachmentsChange}
      />
    ) : (
      <EstimateAttachments
        pendingId={pendingAttachmentId}
        attachments={attachments}
        onAttachmentsChange={onAttachmentsChange}
      />
    );

  const body = (
    <>
      {onScopeOfWorkChange ? (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-sm">
              Scope of work{' '}
              <span className="text-xs text-muted-foreground">
                (short summary of the work - leads the customer document)
              </span>
            </Label>
            <ScopeOfWorkTemplatePicker value={scopeOfWork ?? ''} onInsert={onScopeOfWorkChange} />
          </div>
          <RichTextEditor
            value={scopeOfWork ?? ''}
            onChange={onScopeOfWorkChange}
            placeholder="e.g. Supply and install a 3-ton AC unit on the 2nd floor, including ductwork modifications"
          />
        </div>
      ) : null}
      <div className="space-y-1.5">
        <Label className="text-sm">
          Notes <span className="text-xs text-muted-foreground">(visible on the document PDF)</span>
        </Label>
        <RichTextEditor
          value={notes}
          onChange={onNotesChange}
          placeholder="Add notes visible to the customer..."
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm">
          Attachments{' '}
          <span className="text-xs text-muted-foreground">(visible on the document PDF)</span>
        </Label>
        {attachmentField}
      </div>
    </>
  );

  if (bare) {
    return <div className="space-y-4">{body}</div>;
  }

  return (
    <FormAccordionCard
      open={open}
      header={
        <FormSectionHeader
          icon={Send}
          title="For customer"
          open={open}
          onToggle={onToggle}
          statusBadge={notes || scopeOfWork?.trim() ? 'Added' : undefined}
        />
      }
      bodyClassName="space-y-4"
    >
      {body}
    </FormAccordionCard>
  );
}
